/**
 * Wave 317 · spec-bindings · how `project_specs` becomes a `Program`.
 *
 * `project_specs` is the canonical source of architectural facts (PDFs →
 * D1 by namespace, Wave 269+). The composer doesn't read namespaces
 * directly — it asks for a curated subset (`Program` + `Dimensions`).
 * This file is the only translator. If a project later wants to drive
 * composition with a richer signal, this file is where that lives.
 *
 * Defaults exist so the viewer never crashes if a spec row is missing.
 * Every default is "what Gênese is" so an unfilled project still renders
 * a sensible house.
 */

import type { ProjectSpecs } from '../api';
import type { Program, Dimensions } from './types';

/**
 * Read a spec value as a string (specs are stored as text in D1).
 * `project_specs[namespace][key]` is the access pattern post-Wave 308.
 */
function readSpec(specs: ProjectSpecs, namespace: string, key: string): string | null {
  const ns = (specs as unknown as Record<string, Record<string, string> | undefined>)[namespace];
  if (!ns) return null;
  const v = ns[key];
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

function readSpecBool(specs: ProjectSpecs, namespace: string, key: string, defaultValue: boolean): boolean {
  const raw = readSpec(specs, namespace, key);
  if (raw === null) return defaultValue;
  return raw.toLowerCase() === 'true' || raw === '1' || raw.toLowerCase() === 'sim';
}

function readSpecNumber(specs: ProjectSpecs, namespace: string, key: string, defaultValue: number): number {
  const raw = readSpec(specs, namespace, key);
  if (raw === null) return defaultValue;
  // Accept '4', '4.0', '4,0' (pt-BR), '18 m'
  const cleaned = raw.replace(/[^\d.,-]/g, '').replace(',', '.');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : defaultValue;
}

/**
 * Map specs → Program. Defaults match Gênese, so a project with empty
 * specs still gets a "4-suite L-shape with pool" — visually correct for
 * 80%+ of upper-middle Brazilian residential, which is our target
 * typology. As we add projects with different programs, fill the
 * `program/*` namespace and these defaults get overridden.
 */
export function deriveProgram(specs: ProjectSpecs): Program {
  return {
    suitesCount: Math.max(1, Math.min(8, Math.round(readSpecNumber(specs, 'program', 'suites_count', 4)))),
    hasPool:        readSpecBool(specs, 'program', 'has_pool',         true),
    hasGarage:      readSpecBool(specs, 'program', 'has_garage',       true),
    hasBriseFacade: readSpecBool(specs, 'program', 'has_brise_facade', true),
    hasChimney:     readSpecBool(specs, 'program', 'has_chimney',      true),
    frontFacing:    (readSpec(specs, 'site', 'orientation')?.toLowerCase() as Program['frontFacing']) ?? 'south',
  };
}

/**
 * Map specs → Dimensions. The dimensions namespace is more variable per
 * project — we read everything we can and fall back to Gênese-class
 * proportions for missing values.
 */
export function deriveDimensions(specs: ProjectSpecs): Dimensions {
  return {
    houseLLongM:  readSpecNumber(specs, 'dimensions', 'house_l_long_m',  18),
    houseLShortM: readSpecNumber(specs, 'dimensions', 'house_l_short_m', 12),
    suiteWM:      readSpecNumber(specs, 'dimensions', 'suite_w_m',       3.6),
    suiteDM:      readSpecNumber(specs, 'dimensions', 'suite_d_m',       4.0),
  };
}
